// QC UX feedback — KH không hiểu các technical code (LOGIN_MAX_ATTEMPTS,
// ATTENDANCE_EDIT_WINDOW_DAYS, …). Redesign:
//   - Bỏ cột "Mã" + "Kiểu" (kỹ thuật, user không cần thấy ở primary view).
//   - Cột "Tham số" gộp name_vi (bold) + description (gray helper below).
//   - Mã + Kiểu chuyển vào tooltip / expandable detail cho dev cần debug.
import { InfoCircleOutlined, MailOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Form, Input, message, Modal, Switch, Table, Tooltip, Typography } from 'antd';
import { useMemo, useState } from 'react';

import { axiosClient } from '@/api/axiosClient';

interface SystemParameter {
  code: string;
  name_vi: string;
  value: string;
  value_type: string;
  unit?: string;
  description?: string;
  is_editable: boolean;
}

// T-CFG-02 — 24 params seeded migration 000022. Admin edits via PATCH;
// values cached 30s on BE so consumer services pick up changes quickly.

// Diễn giải tiếng Việt cho từng tham số, hiển thị ở tooltip icon ⓘ.
// Người dùng nghiệp vụ không cần (và không nên) thấy tên biến kỹ thuật, nên
// tooltip chỉ nói ý nghĩa tham số bằng tiếng Việt.
const PARAM_TOOLTIPS: Record<string, string> = {
  ATTENDANCE_EDIT_WINDOW_DAYS:
    'Khoảng thời gian quản lý điểm còn được sửa công của ngày đã qua (tính từ thứ 2 tuần trước tới thứ 2 tuần sau). Hết khoảng này bảng công bị khoá.',
  AUDIT_LOG_RETENTION_DAYS:
    'Số ngày hệ thống lưu lại lịch sử thao tác trước khi tự động xoá. Ví dụ 730 ngày = 2 năm.',
  FORCE_PASSWORD_CHANGE_FIRST_LOGIN:
    'Bật: người dùng bắt buộc đổi mật khẩu ngay trong lần đăng nhập đầu tiên sau khi được cấp / cấp lại mật khẩu.',
  HOTLINE_PHONE_NUMBER:
    'Số điện thoại hiển thị trên app khi người lao động bấm "Quên mật khẩu" để gọi nhờ hỗ trợ cấp lại.',
  HOURS_PER_WORK_DAY:
    'Số giờ làm việc quy đổi thành 1 ngày công. Dùng để đổi giờ chấm công sang ngày công khi tính lương và quyết toán.',
  INITIAL_PASSWORD_LENGTH:
    'Số ký tự của mật khẩu hệ thống tự sinh khi tạo tài khoản mới hoặc cấp lại mật khẩu.',
  JOB_MATCHING_FREE_MONTHS:
    'Với khách hàng thuộc nhóm dịch vụ "Việc làm", hệ thống chỉ thu phí trong số tháng đầu này kể từ ngày người lao động vào làm; sau đó không thu thêm.',
  LEAVE_NP_DEADLINE_HOURS:
    'Người lao động phải gửi đơn nghỉ phép trước ca làm ít nhất số giờ này. Báo trễ hơn sẽ tự động tính là nghỉ không phép.',
  LEAVE_NV_DEADLINE_DAYS:
    'Người lao động phải báo nghỉ việc trước ít nhất số ngày này. Báo trễ hơn sẽ bị tính là nghỉ ngang.',
  LOGIN_LOCK_DURATION:
    'Thời gian tài khoản bị tạm khoá sau khi nhập sai mật khẩu quá số lần cho phép.',
  LOGIN_MAX_ATTEMPTS:
    'Số lần được phép nhập sai mật khẩu liên tiếp trước khi tài khoản bị tạm khoá.',
  MAX_MAKEUP_ATTENDANCE_PER_MONTH:
    'Số lần tối đa người lao động được chấm công bù trong 1 tháng cho những ngày quên chấm công.',
  MAX_UPLOAD_SIZE: 'Dung lượng tối đa cho mỗi tệp tải lên (ảnh CCCD, avatar, file Excel…).',
  PASSWORD_MIN_LENGTH: 'Số ký tự tối thiểu khi người dùng tự đặt mật khẩu mới.',
  PAYROLL_SURVEY_AUTO_OK_AFTER_DEADLINE:
    'Bật: quá hạn xác nhận mà người lao động không phản hồi thì bảng lương được coi là đã đồng ý. Tắt: bảng lương vẫn ở trạng thái chờ phản hồi.',
  PAYROLL_SURVEY_DEADLINE_DAYS:
    'Số ngày người lao động được phép xác nhận bảng lương kể từ khi đợt khảo sát lương được gửi đi.',
  PAYROLL_SURVEY_REMINDER_DAYS:
    'Các mốc ngày hệ thống tự nhắc lại người lao động chưa xác nhận lương, tính từ ngày gửi. Ví dụ [3, 5] là nhắc vào ngày thứ 3 và thứ 5.',
  RATE_JOB_MATCHING_PER_HOUR:
    'Đơn giá thu của khách hàng thuộc nhóm dịch vụ "Việc làm", tính trên mỗi giờ công của người lao động.',
  RATE_RENT_LABOR_PER_HOUR:
    'Đơn giá thu của khách hàng thuộc nhóm dịch vụ "Cho thuê người lao động", tính trên mỗi giờ công.',
  SESSION_TIMEOUT_APP:
    'Thời gian app của người lao động tự đăng xuất khi không thao tác. Giá trị -1 nghĩa là giữ phiên đăng nhập vĩnh viễn.',
  SESSION_TIMEOUT_WEB:
    'Thời gian web quản trị tự đăng xuất khi người dùng không thao tác.',
  SMTP_MAIL_FROM: 'Địa chỉ email hệ thống dùng để gửi thư đi (hiển thị ở mục người gửi).',
  SMTP_MAIL_PASS:
    'Mật khẩu ứng dụng của email gửi thư. Với Gmail cần tạo "App password" thay vì mật khẩu đăng nhập thường.',
  SMTP_MAIL_PORT: 'Cổng kết nối tới máy chủ gửi thư: 465 nếu dùng SSL, 587 nếu dùng TLS.',
  SMTP_MAIL_SERVER: 'Địa chỉ máy chủ gửi thư, ví dụ smtp.gmail.com.',
  URL_PRIVACY_POLICY: 'Đường dẫn trang Chính sách bảo mật hiển thị trên app người lao động.',
  URL_TERMS_OF_SERVICE: 'Đường dẫn trang Điều khoản dịch vụ hiển thị trên app người lao động.',
  WORKING_DAYS_TO_COUNT_HIRED:
    'Số ngày công tối thiểu người lao động phải đi làm thì mới được tính là đạt chỉ tiêu tuyển dụng.',
};

function getParamTooltip(row: SystemParameter) {
  return PARAM_TOOLTIPS[row.code] || row.description || row.name_vi;
}

const GROUPS = [
  { name: 'Cấu hình Email (SMTP)', keys: ['SMTP_'] },
  { name: 'Bảo mật & Phiên làm việc', keys: ['LOGIN_', 'SESSION_'] },
  { name: 'Mật khẩu & Liên hệ', keys: ['PASSWORD_', 'INITIAL_', 'FORCE_', 'HOTLINE_'] },
  { name: 'Chấm công & Nghỉ phép', keys: ['MAX_MAKEUP_', 'LEAVE_', 'ATTENDANCE_', 'WORKING_', 'HOURS_'] },
  { name: 'Khách hàng & Thanh toán', keys: ['JOB_MATCHING_', 'RATE_'] },
  { name: 'Khảo sát lương', keys: ['PAYROLL_'] },
  { name: 'Hệ thống tải lên', keys: ['MAX_UPLOAD_SIZE'] },
  { name: 'Khác', keys: ['URL_', 'AUDIT_'] },
];

function getGroupName(code: string) {
  for (const group of GROUPS) {
    if (group.keys.some((k) => code.startsWith(k))) {
      return group.name;
    }
  }
  return 'Chưa phân loại';
}

const SystemParameterPage = () => {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [testEmailVisible, setTestEmailVisible] = useState(false);
  const [testEmailForm] = Form.useForm();

  const { data, isLoading, refetch } = useQuery(
    ['system-parameter-list'],
    () => axiosClient.get<SystemParameter[]>('/system-parameter/list'),
    { refetchOnWindowFocus: false }
  );

  const updateMutation = useMutation(
    (payload: { code: string; value: string }) =>
      axiosClient.patch('/system-parameter/update', payload),
    {
      onSuccess: () => {
        message.success('Cập nhật tham số thành công');
        refetch();
      },
    }
  );

  const testEmailMutation = useMutation(
    (payload: { email: string; message: string }) =>
      axiosClient.post('/system-parameter/test-email', payload),
    {
      onSuccess: (res: any) => {
        message.success(res.data?.message || 'Gửi email test thành công');
        setTestEmailVisible(false);
        testEmailForm.resetFields();
      },
      onError: (err: any) => {
        const errorMsg = err.getMessage?.() || err.response?.data?.message || err.message || 'Lỗi gửi email';
        message.error(errorMsg);
      }
    }
  );

  function renderValueEditor(row: SystemParameter) {
    if (!row.is_editable) return <span className="text-muted">{row.value}</span>;
    const current = edits[row.code] ?? row.value;
    if (row.value_type === 'BOOLEAN') {
      return (
        <Switch
          checked={current === 'true'}
          onChange={(v) => setEdits({ ...edits, [row.code]: v ? 'true' : 'false' })}
        />
      );
    }

    return (
      <Input
        onChange={(e) => setEdits({ ...edits, [row.code]: e.target.value })}
        size="small"
        style={{ width: 200 }}
        value={current}
      />
    );
  }

  const columns = [
    {
      key: 'param',
      render: (_: unknown, row: SystemParameter) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {row.name_vi}{' '}
            <Tooltip title={getParamTooltip(row)}>
              <InfoCircleOutlined rev="" style={{ color: '#aaa', fontSize: 12 }} />
            </Tooltip>
          </div>
          {row.description && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.description}
            </Typography.Text>
          )}
        </div>
      ),
      title: 'Tham số',
    },
    { key: 'value', render: renderValueEditor, title: 'Giá trị', width: 240 },
    {
      dataIndex: 'unit',
      key: 'unit',
      render: (v?: string) => v || '—',
      title: 'Đơn vị',
      width: 100,
    },
    {
      key: 'save',
      render: (_: unknown, row: SystemParameter) => {
        const changed = edits[row.code] !== undefined && edits[row.code] !== row.value;

        return (
          <Button
            disabled={!changed}
            loading={updateMutation.isLoading}
            onClick={() => updateMutation.mutate({ code: row.code, value: edits[row.code] })}
            size="small"
            type="primary"
          >
            Lưu
          </Button>
        );
      },
      title: '',
      width: 90,
    },
  ];

  const groupedData = useMemo(() => {
    const paramData = (data as unknown as { data?: SystemParameter[] })?.data || [];
    const map = new Map<string, SystemParameter[]>();
    paramData.forEach((p) => {
      const gName = getGroupName(p.code);
      if (!map.has(gName)) map.set(gName, []);
      map.get(gName)!.push(p);
    });
    // Sort groups in the order defined by GROUPS
    return GROUPS.filter((g) => map.has(g.name)).map((g) => ({
      groupName: g.name,
      items: map.get(g.name)!,
    }))
    .concat(
      map.has('Chưa phân loại') ? [{ groupName: 'Chưa phân loại', items: map.get('Chưa phân loại')! }] : []
    );
  }, [data]);

  return (
    <Card
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Di chuột vào biểu tượng ⓘ để xem giải thích chi tiết của từng tham số
        </Typography.Text>
      }
      title="Cấu hình tham số hệ thống"
      loading={isLoading}
    >
      {groupedData.map(({ groupName, items }) => (
        <div key={groupName} className="mb-10">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Typography.Title level={5} style={{ color: '#009ef7', margin: 0 }}>
              {groupName}
            </Typography.Title>
            {groupName === 'Cấu hình Email (SMTP)' && (
              <Button 
                icon={<MailOutlined rev=""/>} 
                onClick={() => setTestEmailVisible(true)} 
                size="small" 
                type="dashed"
              >
                Gửi Test Email
              </Button>
            )}
          </div>
          <Table<SystemParameter>
            columns={columns}
            dataSource={items}
            pagination={false}
            rowKey="code"
            size="small"
            bordered
          />
        </div>
      ))}
      <Modal
        title="Gửi Email Test"
        open={testEmailVisible}
        onCancel={() => setTestEmailVisible(false)}
        onOk={() => testEmailForm.submit()}
        confirmLoading={testEmailMutation.isLoading}
        okText="Gửi"
        cancelText="Hủy"
      >
        <Form
          form={testEmailForm}
          layout="vertical"
          onFinish={(values) => testEmailMutation.mutate(values)}
          initialValues={{ message: 'Đang kiểm tra kết nối SMTP...' }}
        >
          <Form.Item
            name="email"
            label="Email nhận thử nghiệm"
            rules={[
              { required: true, message: 'Vui lòng nhập email nhận!' },
              { type: 'email', message: 'Email không đúng định dạng!' },
            ]}
          >
            <Input placeholder="ví dụ: admin@domain.com" />
          </Form.Item>
          <Form.Item name="message" label="Nội dung kiểm tra (Tuỳ chọn)">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default SystemParameterPage;
